import { cachedDateFormat } from './dateLocale';

/**
 * Display formatting (per BR-18/BR-15: round only at the display edge). Numbers
 * are da-DK in every UI language; dates follow the UI language (ADR 0049).
 * Intl construction is expensive — instances live at module scope.
 */

const hoursFormat = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 });
const amountFormat = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });
/**
 * Both date formats are pinned to UTC. `Date.parse('2026-06-02')` yields UTC
 * midnight, so formatting it in local time renders "01 Jun" anywhere west of
 * Greenwich — a plan date silently off by one for every user in the Americas.
 * A plan date is a calendar day, not an instant; it has no timezone to convert.
 */
const shortDateFormat = cachedDateFormat({
  day: '2-digit',
  month: 'short',
  timeZone: 'UTC',
});
const monthYearFormat = cachedDateFormat({
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * NOT pinned to UTC, and that is the point: this one formats `createdAt` /
 * `updatedAt`, which are real instants (`timestamptz`), not calendar days. An
 * instant genuinely has a timezone, so the reader's own is the right one — the
 * opposite of the rule above.
 */
const timestampFormat = cachedDateFormat({
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

export const formatHours = (value: number): string => hoursFormat.format(value);

export const formatAmount = (value: number): string => amountFormat.format(value);

export const formatPct = (value: number): string => `${Math.round(value)}%`;

const abbrevFormat = new Intl.NumberFormat('da-DK', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Compact amount per BR-15: `1,2M` from one million up, plain da-DK integers
 * below. Sign is preserved (`−0,1M`).
 */
export const formatAmountAbbrev = (value: number): string =>
  Math.abs(value) >= 1_000_000
    ? `${abbrevFormat.format(value / 1_000_000)}M`
    : amountFormat.format(Math.round(value));

const thousandsFormat = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });
const millionsFormat = new Intl.NumberFormat('da-DK', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * A forecast amount in the task panel's KPI box (2026-10-29, the designer's
 * ask): whole units below a million, thousands as `1.080K` from a million up,
 * millions with two decimals as `12,35M` above ten million — all in the app's
 * normal number format (Danish grouping and decimal here; a US format would
 * read `1,080K` / `12.35M`). The box is narrow and the figure sits beside its
 * currency code, so the abbreviation keeps the two on one line. Not
 * `formatAmountAbbrev`: that one goes to one-decimal millions at a million,
 * which reads `1,0M` where the designer wants `1.025K`.
 */
export const formatForecastAmount = (value: number): string => {
  const magnitude = Math.abs(value);
  if (magnitude > 10_000_000) return `${millionsFormat.format(value / 1_000_000)}M`;
  if (magnitude >= 1_000_000) return `${thousandsFormat.format(value / 1_000)}K`;
  return amountFormat.format(Math.round(value));
};

/**
 * Budgeted/target factor as `×2.09` (dot — a multiplier, not a currency
 * amount; matches the design prototype). Null (no cost yet) renders an em dash.
 */
export const formatFactor = (factor: number | null, decimals = 2): string =>
  factor === null ? '—' : `×${factor.toFixed(decimals)}`;

/**
 * A user-entered factor, shown as entered (2026-09-30, the pilot's ask): every
 * decimal that was typed stays (`1.125` → `1.125`), and a whole number gets
 * one decimal so it still reads as a factor (`2` → `2.0`). `toPrecision`
 * scrubs the float noise a stored `1.1` carries. No `×` — the input's value.
 */
export const factorDigits = (factor: number): string =>
  Number.isInteger(factor) ? factor.toFixed(1) : String(parseFloat(factor.toPrecision(12)));

/** `factorDigits` with the `×` prefix, for the pill beside the input. */
export const formatFactorEntered = (factor: number | null): string =>
  factor === null ? '—' : `×${factorDigits(factor)}`;

/** ISO date (YYYY-MM-DD) → "02 Jun"; anything unparsable → an em dash. */
export const formatShortDate = (isoDate: string | undefined): string => {
  if (!isoDate) return '—';
  const parsed = Date.parse(isoDate);
  return Number.isNaN(parsed) ? '—' : shortDateFormat().format(parsed);
};

/**
 * ISO date → "Jun 2026", for the timeline's month band. Formatted in UTC so a
 * date on the 1st cannot render as the previous month west of Greenwich.
 */
export const formatMonthYear = (isoDate: string): string => {
  const parsed = Date.parse(isoDate);
  return Number.isNaN(parsed) ? '—' : monthYearFormat().format(parsed);
};

/** ISO timestamp → "09 Sep 2026" in the reader's timezone; unparsable → an em dash. */
export const formatTimestamp = (isoTimestamp: string | undefined): string => {
  if (!isoTimestamp) return '—';
  const parsed = Date.parse(isoTimestamp);
  return Number.isNaN(parsed) ? '—' : timestampFormat().format(parsed);
};

/** Today as an ISO date, for the shared daysLeft (CALC-6). */
export const todayIso = (): string => new Date().toISOString().slice(0, 10);

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * Parses user date input to ISO (YYYY-MM-DD): accepts ISO itself, "27 Jun",
 * "27 Jun 2026" (English month prefixes, any case). Empty input clears the
 * date (undefined); unparsable input returns null so callers can reject it.
 */
export const parseDateInput = (
  raw: string,
  defaultYear = new Date().getFullYear(),
): string | undefined | null => {
  const text = raw.trim();
  if (text === '' || text === '—') return undefined;

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return text;

  const dayMonth = text.match(/^(\d{1,2})[ .\-/]+([A-Za-z]{3,})\.?(?:[ .\-/]+(\d{4}))?$/);
  if (dayMonth) {
    const day = Number(dayMonth[1]);
    const monthIndex = MONTHS.indexOf(dayMonth[2]!.slice(0, 3).toLowerCase());
    const year = dayMonth[3] ? Number(dayMonth[3]) : defaultYear;
    if (monthIndex < 0 || day < 1 || day > 31) return null;
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  return null;
};

/**
 * Digit grouping applied WHILE someone types an amount, so a million is
 * distinguishable from a thousand at a glance instead of being counted.
 *
 * Only a run of pure digits is grouped. The amount parser also accepts the
 * shorthands `1,2m` and `1.2m`, and regrouping those mid-keystroke would fight
 * the person typing them — so anything that is not digits-plus-separators is
 * returned untouched and left to `parseAmountInput` at commit.
 *
 * Grouped with the string, not `Intl`/`Number`: `Number('007')` is 7, which
 * would delete a leading zero out from under the caret as it was typed.
 */
export const groupDigitsWhileTyping = (raw: string): string => {
  const bare = raw.replace(/[.\s]/g, '');
  if (bare === '' || !/^\d+$/.test(bare)) return raw;
  return bare.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/** How many digits precede `index` — the caret's position in digit-space. */
export const digitsBefore = (text: string, index: number): number =>
  (text.slice(0, index).match(/\d/g) ?? []).length;

/**
 * Where the caret belongs after regrouping: just past the `count`-th digit.
 *
 * Reformatting an input moves every character after the inserted separator, so
 * a caret restored by character offset drifts one place left on every
 * thousands boundary. Counting digits is what keeps it under the cursor.
 */
export const caretAfterDigits = (text: string, count: number): number => {
  if (count === 0) return 0;
  let seen = 0;
  for (let index = 0; index < text.length; index += 1) {
    if (/\d/.test(text[index]!)) {
      seen += 1;
      if (seen === count) return index + 1;
    }
  }
  return text.length;
};
